/**
 * NotificationBell — header-level unread badge for the community inbox.
 *
 * Polls ``/api/community/notifications?unread=true&limit=1`` every 30s
 * to refresh the ``unread_count`` shown in the badge. Click navigates
 * to ``/community/notifications`` (the inbox page lands in a follow-up
 * UI PR).
 *
 * Quietly hides itself if the user isn't a community member (the
 * underlying endpoint returns 403). Mirrors the LoadState pattern from
 * MyJournalPage but with poll-on-interval instead of single-fetch.
 *
 * Per the C0.0 design §7, this lives at the App layout level — one
 * mount, one polling timer, persistent across route changes.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { fetchNotifications } from "@/lib/community-api";

const POLL_INTERVAL_MS = 30_000;

type BellState =
  | { kind: "loading" }
  | { kind: "ready"; unreadCount: number }
  | { kind: "hidden" };

export interface NotificationBellProps {
  /** Test seam — let tests inject a mocked fetcher. */
  fetchFn?: typeof fetchNotifications;
  /** Test seam — disable the polling timer in unit tests. */
  pollIntervalMs?: number;
}

export function NotificationBell({
  fetchFn = fetchNotifications,
  pollIntervalMs = POLL_INTERVAL_MS,
}: NotificationBellProps = {}): React.JSX.Element | null {
  const [state, setState] = useState<BellState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;

    const refresh = async (): Promise<void> => {
      try {
        const resp = await fetchFn({ unread: true, limit: 1 });
        if (cancelled) return;
        setState({ kind: "ready", unreadCount: resp.unread_count });
      } catch (err) {
        if (cancelled) return;
        // 403 = not a community member → hide the bell entirely.
        // 401 = unauthenticated → also hide; the page itself handles
        // re-auth flow.
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403)
        ) {
          setState({ kind: "hidden" });
          return;
        }
        // Other errors — keep the bell visible but stop showing a stale
        // count by resetting to 0. The next poll will retry.
        setState({ kind: "ready", unreadCount: 0 });
      }
    };

    void refresh();
    const id = window.setInterval(() => {
      void refresh();
    }, pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchFn, pollIntervalMs]);

  if (state.kind === "hidden") {
    return null;
  }
  if (state.kind === "loading") {
    return (
      <span
        className="text-muted-foreground"
        aria-busy="true"
        aria-label="Loading notifications"
      >
        🔔
      </span>
    );
  }

  const count = state.unreadCount;
  return (
    <Link
      to="/community/notifications"
      className="relative inline-flex items-center hover:text-foreground transition-colors"
      aria-label={
        count > 0
          ? `Notifications, ${count} unread`
          : "Notifications, none unread"
      }
    >
      <span aria-hidden="true">🔔</span>
      {count > 0 && (
        <span
          className="absolute -right-2 -top-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground"
          aria-hidden="true"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
